import { Box, IconButton, Typography } from '@mui/material'
import { EditOutlined } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { AccessStatus } from '../../../../utils/genericTypes'
import { AuthFunctions, hasPermission } from '../../../../utils/permissions'
import { BlockButton } from '../../../customComponents/buttons/BlockButton'
import { BlueButton } from '../../../customComponents/buttons/BlueButton'
import { ProfileField } from './ProfileField'
import { ProfileImage } from '../../../customComponents/user/ProfileImage'
import { US_STATE_CODES } from './usStates'
import { styles } from './UserInformationCard.styles'
import { useAuth } from '../../../../hooks/use-auth'
import type { Address } from '../../../../models/Address'
import type { User } from '../../../../models/User'

export interface UserInformationFormValues {
  id: number
  firstName: string
  lastName: string
  title: string
  email: string
  phoneNumber: string
  address: Address
}

interface Props {
  user: User
  canEdit: boolean
  allowEditEmail?: boolean
  onDirtyChange?: (isDirty: boolean) => void
  onSave: (form: UserInformationFormValues) => Promise<void>
  onUploadImage: (file: File) => Promise<string | null>
}

const emptyAddress: Address = { id: 0, street: '', city: '', state: '', zip: '', apartment: '' }

const formValuesFor = (user: User): UserInformationFormValues => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  title: user.title ?? '',
  email: user.email,
  phoneNumber: user.phoneNumber ?? '',
  address: { ...emptyAddress, ...user.address, apartment: user.address?.apartment ?? '' }
})

export const UserInformationCard = (props: Props): JSX.Element => {
  const auth = useAuth()
  const [form, setForm] = useState<UserInformationFormValues>(formValuesFor(props.user))
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [uploadedImageKey, setUploadedImageKey] = useState<string | null>(null)

  useEffect(() => {
    setForm(formValuesFor(props.user))
    setIsDirty(false)
  }, [props.user])

  useEffect(() => {
    props.onDirtyChange?.(isDirty)
  }, [isDirty])

  const canImpersonate = props.canEdit &&
    auth.user != null &&
    !auth.user.isImpersonating &&
    props.user.id > 0 &&
    props.user.accessStatus === AccessStatus.Active &&
    auth.user.id !== props.user.id &&
    hasPermission(auth.user, AuthFunctions.IMPERSONATION)

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    setIsDirty(true)
  }

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setForm((prev) => ({ ...prev, address: { ...prev.address, [e.target.name]: e.target.value } }))
    setIsDirty(true)
  }

  const handleCancel = (): void => {
    setForm(formValuesFor(props.user))
    setIsDirty(false)
  }

  const handleSave = async (): Promise<void> => {
    setIsSaving(true)
    await props.onSave(form)
    setIsSaving(false)
    setIsDirty(false)
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.item(0)
    if (file == null) return

    setUploadedImageKey(await props.onUploadImage(file))
  }

  return <Box sx={styles.panel} data-testid='userInformationPanel'>
    <Typography variant='h4' sx={styles.panelHeader}>User Information</Typography>

    <Box sx={styles.card} data-testid='userInformationCard'>
      <Box sx={styles.identity} data-testid='profileImageSection'>
        <Box sx={styles.avatarWrapper}>
          <ProfileImage
            name={props.user.fullName}
            imageKey={uploadedImageKey ?? props.user.imageKey}
            height='8em'
            width='8em'
          />

          {props.canEdit && <IconButton
            component='label'
            aria-label='Change Profile Image'
            sx={styles.avatarEditButton}
          >
            <EditOutlined sx={{ fill: 'black' }} />
            <input hidden type='file' name='file' onChange={handleFileSelect} />
          </IconButton>}
        </Box>

        <Typography variant='h4' sx={styles.name} data-testid='fullName'>
          {`${form.firstName} ${form.lastName}`}
        </Typography>

        {canImpersonate && <BlueButton onClick={async () => { await auth.impersonate(props.user.id) }} dataTestId='impersonate'>
          Impersonate
        </BlueButton>}
      </Box>

      <Box sx={styles.fieldRow}>
        <ProfileField name='firstName' label='First Name' value={form.firstName} disabled={!props.canEdit} onChange={handleFieldChange} />
        <ProfileField name='lastName' label='Last Name' value={form.lastName} disabled={!props.canEdit} onChange={handleFieldChange} />
      </Box>

      <ProfileField name='title' label='Title' value={form.title} disabled={!props.canEdit} onChange={handleFieldChange} />
      <ProfileField
        name='email'
        label='Email'
        value={form.email}
        disabled={!props.canEdit || props.allowEditEmail === false}
        helperText={props.allowEditEmail === false ? 'Your MiCredentials sign-in — contact an administrator to change it' : undefined}
        onChange={handleFieldChange}
      />
      <ProfileField name='phoneNumber' label='Phone' value={form.phoneNumber} disabled={!props.canEdit} onChange={handleFieldChange} />
      <ProfileField name='street' label='Address' value={form.address.street} disabled={!props.canEdit} onChange={handleAddressChange} />
      <ProfileField name='apartment' label='Address line 2' value={form.address.apartment ?? ''} disabled={!props.canEdit} onChange={handleAddressChange} />
      <ProfileField name='city' label='City' value={form.address.city} disabled={!props.canEdit} onChange={handleAddressChange} />

      <Box sx={styles.fieldRow}>
        <ProfileField name='state' label='State' value={form.address.state} options={US_STATE_CODES} disabled={!props.canEdit} onChange={handleAddressChange} />
        <ProfileField name='zip' label='ZIP' value={form.address.zip} disabled={!props.canEdit} onChange={handleAddressChange} />
      </Box>

      {props.canEdit && isDirty && <Box sx={styles.actions} data-testid='userInformationActions'>
        <BlueButton onClick={() => { void handleSave() }} disabled={isSaving} dataTestId='saveUserInformation'>
          Save Changes
        </BlueButton>

        <BlockButton onClick={handleCancel} dataTestId='cancelUserInformation'>
          Cancel
        </BlockButton>
      </Box>}
    </Box>
  </Box>
}
